/**
 * ApplyAllProgressModal — shows while Apply All runs, auto-navigates on success.
 *
 * Phase: submitting  → indeterminate spinner, "Applying your answers…"
 * Phase: in_progress → progress bar + single cycling status line
 * Phase: completed   → progress bar turns green, "Done!" for 1s, then auto-continue
 * Phase: failed      → error message, Retry + Close buttons
 */
import React, { JSX, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import type {
  ApplyAllStep,
  ApplyAllPhase,
} from 'src/hooks/aiFileUploadV2/useApplyAllProgress';

interface Props {
  open: boolean;
  phase: ApplyAllPhase;
  steps: ApplyAllStep[];
  currentStepIndex: number;
  error: string | null;
  /** Called when user clicks "Continue to Interview". */
  onContinue: () => void;
  /** Called when user clicks "Try again" after failure. */
  onRetry: () => void;
  /** Called when user clicks "Close" after failure. */
  onClose: () => void;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ApplyAllProgressModal({
  open,
  phase,
  steps,
  currentStepIndex,
  error,
  onContinue,
  onRetry,
  onClose,
}: Props): JSX.Element {
  const theme = useTheme();
  const hasAutoNavigatedRef = useRef(false);
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;

  // Auto-navigate after a brief "Done!" display so the renter registers success.
  useEffect(() => {
    if (phase === 'completed' && !hasAutoNavigatedRef.current) {
      hasAutoNavigatedRef.current = true;
      const timer = setTimeout(() => {
        onContinueRef.current();
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (phase !== 'completed') {
      hasAutoNavigatedRef.current = false;
    }
  }, [phase]);

  // Reset auto-navigate guard when modal re-opens.
  useEffect(() => {
    if (!open) {
      hasAutoNavigatedRef.current = false;
    }
  }, [open]);

  const isSubmitting = phase === 'submitting';
  const isInProgress = phase === 'in_progress';
  const isCompleted = phase === 'completed';
  const isFailed = phase === 'failed';

  // Progress fraction for the thin progress bar (0–1).
  const progressFraction = isCompleted
    ? 1
    : steps.length > 0
    ? (currentStepIndex + 1) / steps.length
    : 0;

  return (
    <Dialog
      open={open}
      onClose={undefined} // prevent dismiss while in-progress
      disableEscapeKeyDown={!isFailed && !isCompleted}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 420,
          width: '100%',
          mx: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Thin animated progress bar at top */}
      <Box
        sx={{
          height: 4,
          backgroundColor: theme.palette.action.hover,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${Math.round(progressFraction * 100)}%`,
            backgroundColor: isFailed
              ? theme.palette.error.main
              : theme.palette.primary.main,
            transition: 'width 0.5s ease, background-color 0.4s ease',
          }}
        />
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* ── Submitting ── */}
        {isSubmitting && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}
            >
              Applying your answers…
            </Typography>
          </Box>
        )}

        {/* ── In progress ── */}
        {isInProgress && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}
            >
              {currentStepIndex >= 0 && steps[currentStepIndex]
                ? steps[currentStepIndex].label
                : 'Preparing your answers…'}
            </Typography>
          </Box>
        )}

        {/* ── Completed ── */}
        {isCompleted && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}
            >
              Done!
            </Typography>
          </Box>
        )}

        {/* ── Failed ── */}
        {isFailed && (
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
            >
              <ErrorOutlineIcon
                sx={{ fontSize: 28, color: theme.palette.error.main }}
              />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Something went wrong
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                color: theme.palette.text.secondary,
                mb: 3,
                wordBreak: 'break-word',
              }}
            >
              {error ?? 'An unexpected error occurred. Please try again.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={onRetry}
                sx={{ flex: 1, height: 48, borderRadius: 2, fontWeight: 600 }}
                disableElevation
              >
                Try again
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  height: 48,
                  borderRadius: 2,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
